import * as React from "react";

import { Box, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import * as Redux from "react-redux";

import { MopButton } from "client/components";
import {
  FormCollectionManager,
  FormCollectionModalModeMap,
  FormCollectionProvider,
  useFormCollection,
} from "client/domain";
import { useBranding, useMerchantModel, useTranslation } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { BusinessType, CountryList } from "client/lib/apt-merchant";
import { errorActions, RootState } from "client/store";

import { MerchantAgreementForm } from "../MerchantAgreementForm";
import { StaticFieldRenderer } from "../StaticFieldRenderer";

import { businessOwnerUiFieldMap, FormWrapper } from "./FormWrapper";
import { useSubmitBusinessOwner } from "./useSubmitBusinessOwner";

const useStyles = makeStyles((theme: Theme) => {
  const { gray30 } = useNormalizedBrandPaletteValues(theme);

  return {
    addOwnerButton: {
      minWidth: "50%",
    },
    modalRoot: {
      maxWidth: 648,
      padding: theme.spacing(6, 10),
    },
    description: {
      fontSize: "0.9rem",
      lineHeight: 1.5,
      color: gray30,
    },
  };
});

const allowAdditionalBusinessOwners = ({
  businessType,
  registeredCountry,
}: {
  businessType: BusinessType;
  registeredCountry: CountryList;
}) => {
  let allow = true;
  if (registeredCountry === "Australia") {
    allow = false;
  } else if (
    (registeredCountry === "United States" || registeredCountry === "Canada") &&
    businessType === "Sole Proprietorship"
  ) {
    allow = false;
  } else if (
    (registeredCountry === "New Zealand" ||
      registeredCountry === "United Kingdom") &&
    businessType === "Sole Trader"
  ) {
    allow = false;
  }
  return allow;
};

export const BusinessOwnerForm: React.FC = () => {
  const { merchant } = useMerchantModel();
  const initialState = {
    canAddToCollection: true,
    completedFieldSetCount: 1,
    collection: merchant.owners,
    currentFieldSetIndex: 0,
  };

  return (
    <FormCollectionProvider initialState={initialState}>
      <BusinessOwnerFormContents />
    </FormCollectionProvider>
  );
};

export const BusinessOwnerFormContents: React.FC = () => {
  const [isAgreementAccepted, setIsAgreementAccepted] = React.useState(false);
  const { merchant, validateMerchant } = useMerchantModel();
  const dispatch = Redux.useDispatch();
  const t = useTranslation();
  const formCompletionState = Redux.useSelector(
    ({ formCompletion }: RootState) => formCompletion
  );

  const { brandKey } = useBranding();
  const isCash = brandKey === "CashApp";

  const {
    canAddToCollection,
    completedFieldSetCount,
    currentFieldSetIndex,
    handleAddToCollection,
    handleRemoveFromCollection,
    modalMode,
    setCanAddToCollection,
    setModalState,
  } = useFormCollection();

  const { completeBusinessOwners } = useSubmitBusinessOwner(
    isAgreementAccepted
  );

  React.useEffect(() => {
    setIsAgreementAccepted(formCompletionState.agreementAccepted);
  }, [formCompletionState.agreementAccepted]);

  React.useEffect(() => {
    if (
      merchant.companyInformation.businessType.value &&
      merchant.companyInformation.registeredCountry.value
    ) {
      const canAdd = allowAdditionalBusinessOwners({
        businessType: merchant.companyInformation.businessType.value,
        registeredCountry: merchant.companyInformation.registeredCountry.value,
      });
      setCanAddToCollection(canAdd);
    }
  }, [
    merchant.companyInformation.businessType.value,
    merchant.companyInformation.registeredCountry.value,
    setCanAddToCollection,
  ]);

  const classes = useStyles();

  /**
   * Validate first owner before allowing additional owners
   */
  const addAdditionalBusinessOwnerFromInlineForm = () => {
    const isValid = validateMerchant(merchant.owners[0]);
    if (isValid) {
      handleAddToCollection("owners");
    } else {
      dispatch(
        errorActions.setError(
          "You have missing or invalid business owner fields"
        )
      );
    }
  };

  const getLabel = (idx: number): string =>
    `${merchant.owners?.[idx]?.firstName?.value ?? ""} ${
      merchant.owners?.[idx]?.lastName?.value ?? ""
    }`;

  const nzNomineeBusinessOwnerLabel = (): string => {
    const businessType = merchant.companyInformation.businessType.value;
    const registeredCountry =
      merchant.companyInformation.registeredCountry.value;

    if (
      businessType === "Private Company" &&
      registeredCountry == "New Zealand"
    ) {
      return t("whatIsAnAdditionalBusinessOwnerWithNominee");
    }

    return t("whatIsAnAdditionalBusinessOwner");
  };

  const EditableModalBody = () => (
    <Box mb={2}>
      <FormWrapper
        agreementAccepted={isAgreementAccepted}
        handleModalClose={() => setModalState("closed")}
        owner={merchant.owners[currentFieldSetIndex]}
        ownerIndex={currentFieldSetIndex}
        submitLabel={formCollectionModalMap[modalMode].primaryActionText}
      />
    </Box>
  );

  const DeleteModalBody = () => (
    <Box mb={2}>
      <Box mb={2}>
        <StaticFieldRenderer
          fieldSet={merchant.owners[currentFieldSetIndex]}
          uiFieldMap={businessOwnerUiFieldMap?.owners?.[currentFieldSetIndex]}
        />
      </Box>
      <MopButton
        mopVariant="primary"
        fullWidth
        onClick={() =>
          handleRemoveFromCollection("owners", currentFieldSetIndex)
        }
      >
        {formCollectionModalMap[modalMode].primaryActionText}
      </MopButton>
    </Box>
  );

  const formCollectionModalMap: FormCollectionModalModeMap = {
    add: {
      headerText: t("addAdditionalBusinessOwner"),
      modalBody: EditableModalBody,
      primaryActionText: t("add"),
    },
    delete: {
      headerText: `Are you sure you want to delete "${getLabel(
        currentFieldSetIndex
      )}"`,
      modalBody: DeleteModalBody,
      primaryActionText: t("uiDeleteModalConfirm"),
    },
    edit: {
      headerText: `Edit "${getLabel(currentFieldSetIndex)}"`,
      modalBody: EditableModalBody,
      primaryActionText: t("uiEditModalSaveChanges"),
    },
  };

  if (completedFieldSetCount < 2) {
    return (
      <Box mb={2}>
        <FormWrapper
          agreementAccepted={isAgreementAccepted}
          formFooter={
            <Box>
              {canAddToCollection && (
                <Box mt={3}>
                  <Box mb={2}>
                    <Typography variant="body2">
                      <strong>{t("additionalBusinessOwnersHeader")}</strong>
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" className={classes.description}>
                      {nzNomineeBusinessOwnerLabel()}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <MopButton
                      className={classes.addOwnerButton}
                      mopVariant="secondary"
                      fullWidth={isCash}
                      startIcon={
                        isCash ? <Typography>+</Typography> : undefined
                      }
                      onClick={() => {
                        addAdditionalBusinessOwnerFromInlineForm();
                        logAmplitudeEvent(
                          "REDESIGN_ADD_YOUR_STORE",
                          "clicked_add_business_owners_button"
                        );
                      }}
                    >
                      {t("addAdditionalBusinessOwners")}
                    </MopButton>
                  </Box>
                </Box>
              )}

              <MerchantAgreementForm
                isAgreementAccepted={isAgreementAccepted}
                onChange={(_, checked) => {
                  setIsAgreementAccepted(checked);

                  if (checked) {
                    logAmplitudeEvent(
                      "REDESIGN_ADD_YOUR_STORE",
                      "checked_merchant_agreement_form_checkbox"
                    );
                  }
                }}
                disableCheckbox={formCompletionState.agreementAccepted}
                product={"Afterpay"}
              />
            </Box>
          }
          owner={merchant.owners[0]}
          ownerIndex={0}
          shouldSaveOnSubmit={true}
        />
      </Box>
    );
  }

  return (
    <Box mb={2}>
      <Box mb={2}>
        <FormCollectionManager
          additionalLinkText={`+ ${t("addAdditionalBusinessOwner")}`}
          allowAdditional={completedFieldSetCount < 5}
          collection={merchant.owners}
          collectionType="owners"
          getLabel={getLabel}
          modalBody={formCollectionModalMap[modalMode].modalBody}
          modalHeader={
            <Typography variant="body2">
              <strong>{formCollectionModalMap[modalMode].headerText}</strong>
            </Typography>
          }
        />
      </Box>
      <MerchantAgreementForm
        isAgreementAccepted={isAgreementAccepted}
        onChange={(_, checked) => {
          setIsAgreementAccepted(checked);
          if (checked) {
            logAmplitudeEvent(
              "REDESIGN_ADD_YOUR_STORE",
              "checked_merchant_agreement_form_checkbox"
            );
          }
        }}
        disableCheckbox={formCompletionState.agreementAccepted}
        product={"Afterpay"}
      />
      <Box mt={3}>
        <Grid xs={12} item>
          <MopButton
            mopVariant="primary"
            fullWidth
            onClick={completeBusinessOwners}
          >
            Next
          </MopButton>
        </Grid>
      </Box>
    </Box>
  );
};
